import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import { X } from "lucide-react";
import type { ReactNode } from "react";

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Panel title, in plain language. */
  title: string;
  /** Muted helper line under the title. */
  description?: string;
  /** Footer row, typically the submit and cancel actions. */
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Edge-anchored panel for a focused task beside the current page — the
 * inline-end counterpart to `<Dialog>`. Built on the same primitive, so it
 * traps focus, locks background scroll, and closes on Escape or an overlay
 * press.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  footer,
  className,
  children,
}: DrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[var(--overlay)]" />
        <DialogPrimitive.Content
          className={cn(
            "surface fixed inset-y-0 end-0 z-50 flex w-[min(28rem,100%)] flex-col gap-4 overflow-hidden border-s border-[var(--border)] p-6 focus:outline-none",
            className,
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-lg font-semibold tracking-tight">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-xs text-[var(--muted-foreground)]">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close panel">
                <X className="size-4" />
              </Button>
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pe-1">{children}</div>
          {footer ? <div className="actions-row">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
